import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'prowycena.app@gmail.com';

function PublicInfoShell({ title, eyebrow, children }: { title: string; eyebrow: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[#0b0d10] text-slate-50">
      <header className="border-b border-white/10 bg-[#0b0d10]">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/" className="text-xl font-black tracking-tight text-white">
            ProWycena<span className="text-amber-400">app.pl</span>
          </Link>
          <Link
            to="/app/wycena"
            className="min-h-11 rounded-md bg-emerald-500 px-4 py-2 text-sm font-black text-[#07110b] transition hover:bg-emerald-400"
          >
            Zrób wycenę
          </Link>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-12 md:py-16">
        <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">{eyebrow}</p>
        <h1 className="mt-3 text-3xl font-black tracking-tight text-white md:text-5xl">{title}</h1>
        <div className="mt-8 rounded-sm border border-[#2a2f38] bg-[#11151b] p-6 text-sm leading-7 text-slate-300 md:p-8">
          {children}
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#080a0d] py-8">
        <div className="container mx-auto flex flex-col gap-3 px-4 text-sm text-slate-400 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-bold text-slate-200">ProWycenaapp.pl</p>
            <p>Wyceny mają charakter roboczy i wymagają potwierdzenia zakresu oraz powierzchni przed rozpoczęciem prac.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/o-produkcie" className="hover:text-amber-200">O produkcie</Link>
            <Link to="/tarcza-marzy" className="hover:text-amber-200">Tarcza Marży</Link>
            <Link to="/faq" className="hover:text-amber-200">FAQ</Link>
            <Link to="/kontakt" className="hover:text-amber-200">Kontakt</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-amber-200">Polityka prywatności</Link>
            <Link to="/regulamin" className="hover:text-amber-200">Regulamin testów beta</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function ContactPage() {
  return (
    <PublicInfoShell eyebrow="Kontakt" title="Kontakt">
      <p>W sprawach dotyczących testów beta ProWycena prosimy o kontakt mailowy:</p>
      <p className="mt-4 text-lg font-black text-white">
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-200 hover:underline">
          {CONTACT_EMAIL}
        </a>
      </p>
      <p className="mt-6">
        ProWycena jest obecnie projektem testowym. Pierwsze wyceny mają charakter roboczy i służą sprawdzeniu narzędzia z wykonawcami.
      </p>
    </PublicInfoShell>
  );
}

export function BetaTermsPage() {
  return (
    <PublicInfoShell eyebrow="Regulamin" title="Regulamin testów beta">
      <ul className="space-y-4">
        <li>ProWycena jest projektem beta testowanym z wykonawcami robót wykończeniowych.</li>
        <li>Pierwsze wyceny testowe są bez opłaty, dopóki płatności nie są wdrożone w aplikacji.</li>
        <li>Wyceny mają charakter roboczy i wymagają sprawdzenia przed użyciem w rozmowie z klientem.</li>
        <li>Metraż, zakres prac i cenę końcową zatwierdza wykonawca.</li>
        <li>Tarcza Marży pokazuje pozycje do sprawdzenia, ale nie narzuca decyzji ani nie dodaje kosztów bez akceptacji użytkownika.</li>
        <li>Zdjęcie jest kontekstem dla zakresu i stanu prac, nie miarką ani samodzielnym źródłem metrażu.</li>
        <li>ProWycena nie generuje formalnego kosztorysu KNR w wersji beta.</li>
        <li>
          Kontakt w sprawach testów beta:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold text-amber-200 hover:underline">
            {CONTACT_EMAIL}
          </a>
        </li>
      </ul>
    </PublicInfoShell>
  );
}

export function BetaPrivacyPage() {
  return (
    <PublicInfoShell eyebrow="Prywatność" title="Polityka prywatności wersji testowej">
      <ul className="space-y-4">
        <li>ProWycena jest projektem beta i może być aktualizowana w trakcie testów.</li>
        <li>
          Kontakt w sprawach prywatności:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-bold text-amber-200 hover:underline">
            {CONTACT_EMAIL}
          </a>
        </li>
        <li>Możliwe dane przetwarzane w aplikacji: e-mail, dane wyceny, dane projektu, dane techniczne oraz opcjonalne zdjęcia dodane przez użytkownika.</li>
        <li>Cele przetwarzania: działanie aplikacji, zapis projektu, generowanie PDF, kontakt w ramach testów beta i poprawa narzędzia.</li>
        <li>Usługi zewnętrzne mogą obejmować hosting, Firebase/Google, logowanie i usługi AI używane do pomocniczych sugestii.</li>
        <li>Użytkownik może poprosić o dostęp do danych, poprawienie danych lub usunięcie danych.</li>
      </ul>
    </PublicInfoShell>
  );
}
